/* Danachi 2026 - WebGL broken-heart hero.

   Clipping planes in three.js are WORLD space and are not moved by an object's
   transform, so translating the halves alone would re-clip both at x=0 and the
   heart would never actually open. The gap is driven by the plane constants and
   the mesh positions together, in lockstep. The rotation/scale are baked into
   the geometry so no object transform can invalidate the planes.

   Interaction: drag to turn the heart, release to keep the spin, click to
   break it open. */
import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.module.js';

export function mountDanachiHero(root) {
  var canvas = root.querySelector('[data-d3d-canvas]');
  if (!canvas) return null;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var mobile = window.matchMedia('(max-width: 749px)').matches;

  var split = parseFloat(root.dataset.split || '0.16');
  if (!isFinite(split)) split = 0.16;
  var glow = parseFloat(root.dataset.glow || '1');
  if (!isFinite(glow)) glow = 1;
  var crimson = new THREE.Color(root.dataset.crimson || '#c1121f');
  var violet = new THREE.Color(root.dataset.violet || '#7b5cff');

  var renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true, powerPreference: 'high-performance' });
  /* clearcoat + DoubleSide over a full-bleed hero is fill-rate heavy; 2x DPR on
     a 4K panel is what turns a smooth orbit into a stuttering one */
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, mobile ? 1.5 : 1.75));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.15;
  renderer.localClippingEnabled = true;

  var scene = new THREE.Scene();

  var camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
  camera.position.set(0, 0, 7.2);

  /* ---- heart geometry (transform baked in) ---- */
  var shape = new THREE.Shape();
  shape.moveTo(0.5, 0.5);
  shape.bezierCurveTo(0.5, 0.5, 0.4, 0, 0, 0);
  shape.bezierCurveTo(-0.6, 0, -0.6, 0.7, -0.6, 0.7);
  shape.bezierCurveTo(-0.6, 1.1, -0.3, 1.54, 0.5, 1.9);
  shape.bezierCurveTo(1.2, 1.54, 1.6, 1.1, 1.6, 0.7);
  shape.bezierCurveTo(1.6, 0.7, 1.6, 0, 1.0, 0);
  shape.bezierCurveTo(0.7, 0, 0.5, 0.5, 0.5, 0.5);

  var geo = new THREE.ExtrudeGeometry(shape, {
    depth: 0.42,
    bevelEnabled: true,
    bevelThickness: 0.09,
    bevelSize: 0.09,
    bevelSegments: 5,
    curveSegments: mobile ? 18 : 32
  });
  geo.center();
  geo.rotateZ(Math.PI); /* the shape is drawn point-up; flip it so it reads as a heart */
  geo.scale(1.5, 1.5, 1.5);

  var edgeGeo = new THREE.EdgesGeometry(geo, 28);

  /* planeL keeps world x <= constant, planeR keeps world x >= -constant */
  var planeL = new THREE.Plane(new THREE.Vector3(-1, 0, 0), 0);
  var planeR = new THREE.Plane(new THREE.Vector3(1, 0, 0), 0);

  function halfMaterial(planes) {
    return new THREE.MeshPhysicalMaterial({
      color: 0x0a090c,
      metalness: 0.4,
      roughness: 0.16,
      clearcoat: 1,
      clearcoatRoughness: 0.14,
      emissive: 0x1e0000,
      emissiveIntensity: 0.7 * glow,
      side: THREE.DoubleSide,
      clippingPlanes: planes
    });
  }
  function edgeMaterial(planes) {
    return new THREE.LineBasicMaterial({
      color: crimson,
      transparent: true,
      opacity: 0.9,
      clippingPlanes: planes
    });
  }

  var materials = [];
  var heart = new THREE.Group();

  var mL = halfMaterial([planeL]);
  var eL = edgeMaterial([planeL]);
  var left = new THREE.Group();
  left.add(new THREE.Mesh(geo, mL));
  left.add(new THREE.LineSegments(edgeGeo, eL));

  var mR = halfMaterial([planeR]);
  var eR = edgeMaterial([planeR]);
  var right = new THREE.Group();
  right.add(new THREE.Mesh(geo, mR));
  right.add(new THREE.LineSegments(edgeGeo, eR));

  materials.push(mL, eL, mR, eR);
  heart.add(left, right);
  scene.add(heart);

  /* ---- embers ---- */
  var count = mobile ? 220 : 520;
  var pos = new Float32Array(count * 3);
  var seed = new Float32Array(count);
  for (var i = 0; i < count; i++) {
    pos[i * 3] = (Math.random() - 0.5) * 14;
    pos[i * 3 + 1] = (Math.random() - 0.5) * 10;
    pos[i * 3 + 2] = (Math.random() - 0.5) * 8 - 1;
    seed[i] = Math.random();
  }
  var pGeo = new THREE.BufferGeometry();
  pGeo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  var emberMat = new THREE.PointsMaterial({
    color: crimson,
    size: mobile ? 0.045 : 0.038,
    transparent: true,
    opacity: 0.75,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    fog: false
  });
  var embers = new THREE.Points(pGeo, emberMat);
  materials.push(emberMat);
  scene.add(embers);

  /* ---- light ---- */
  scene.add(new THREE.AmbientLight(0x140004, 1.2));
  var key = new THREE.PointLight(crimson, 26 * glow, 24, 2);
  key.position.set(3, 2.4, 4);
  scene.add(key);
  var fill = new THREE.PointLight(violet, 10 * glow, 22, 2);
  fill.position.set(-4, -1.6, 3);
  scene.add(fill);
  var rim = new THREE.DirectionalLight(0xede7e3, 0.75);
  rim.position.set(-2, 3, -5);
  scene.add(rim);

  /* ---------------- interaction ---------------- */
  var target = { x: 0, y: 0 };
  var pointer = { x: 0, y: 0 };

  var spin = 0.5;       /* rendered orbit angle */
  var spinTo = 0.5;     /* where drag + inertia + idle want it */
  var spinVel = 0;      /* radians per SECOND, so throws are frame-rate stable */
  var dragDX = 0;       /* pointer delta accumulated since the last frame */
  var dragDY = 0;
  var parallax = 1;     /* cursor-follow strength, fades out while dragging */
  var dragging = false;
  var dragged = false;
  var lastX = 0;
  var lastY = 0;
  var tiltY = 0;        /* vertical drag raises/lowers the camera */
  var burst = 0;        /* click impulse that cracks the heart wide open */

  /* pointermove can fire several times between frames. Mutating the camera
     angle inside the handler made the throw depend on whichever delta landed
     last; accumulate instead and integrate once per frame. */
  function onMove(e) {
    var r = root.getBoundingClientRect();
    target.x = ((e.clientX - r.left) / r.width - 0.5) * 2;
    target.y = ((e.clientY - r.top) / r.height - 0.5) * 2;

    if (!dragging) return;
    var dx = e.clientX - lastX;
    var dy = e.clientY - lastY;
    lastX = e.clientX;
    lastY = e.clientY;
    if (Math.abs(dx) + Math.abs(dy) > 2) dragged = true;
    dragDX += dx;
    dragDY += dy;
  }

  function onDown(e) {
    dragging = true;
    dragged = false;
    lastX = e.clientX;
    lastY = e.clientY;
    dragDX = 0;
    dragDY = 0;
    spinVel = 0;
    root.classList.add('is-grabbing');
    if (canvas.setPointerCapture && e.pointerId !== undefined) {
      try { canvas.setPointerCapture(e.pointerId); } catch (err) {}
    }
  }

  function onUp() {
    dragging = false;
    root.classList.remove('is-grabbing');
  }

  function onClick() {
    if (dragged) return;
    burst = 1;
  }

  function onLeave() { target.x = 0; target.y = 0; }

  canvas.addEventListener('pointerdown', onDown);
  root.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp);
  /* On touch the page scroller can steal a gesture that began on the canvas and
     fire pointercancel INSTEAD of pointerup. Without this the drag flag stays
     true forever and the idle spin - which lives in the else branch below -
     never runs again for the rest of the visit. */
  window.addEventListener('pointercancel', onUp);
  canvas.addEventListener('click', onClick);
  root.addEventListener('pointerleave', onLeave);

  /* PerspectiveCamera.fov is VERTICAL. On a portrait phone the horizontal field
     collapses - at aspect 0.52 the visible half-width was 1.30 against a heart
     whose half-width is 1.65, so the artwork ran off both sides and read as two
     black lobes rather than a heart. Pull the camera back until the full width
     fits. Landscape is untouched: the max() keeps the tuned 7.2. */
  var FIT_HALF_WIDTH = 2.1;
  var fitRadius = 7.2;

  function resize() {
    var w = root.clientWidth;
    var h = root.clientHeight;
    if (!w || !h) return;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    var vHalf = (camera.fov * Math.PI) / 360;
    var hHalf = Math.atan(Math.tan(vHalf) * camera.aspect);
    fitRadius = Math.max(7.2, FIT_HALF_WIDTH / Math.tan(hHalf));
  }
  var ro = new ResizeObserver(resize);
  ro.observe(root);
  resize();

  var visible = true;
  var io = new IntersectionObserver(function (entries) {
    visible = entries[0].isIntersecting;
  }, { threshold: 0.02 });
  io.observe(root);

  var clock = new THREE.Clock();
  var raf = 0;
  var disposed = false;
  var prevT = 0;

  function frame() {
    if (disposed) return;
    raf = requestAnimationFrame(frame);
    if (!visible) return;

    /* getElapsedTime() consumes the clock's delta internally, so a following
       getDelta() would return ~0 - derive dt from the elapsed time instead */
    var t = clock.getElapsedTime();
    var dt = Math.min(0.05, Math.max(0.0001, t - prevT));
    prevT = t;

    /* frame-rate independent easing: the same visual speed at 60, 120 or 144Hz.
       A raw per-frame multiplier made the motion feel different on every
       machine and choppy whenever the frame budget slipped. */
    function damp(rate) { return 1 - Math.pow(1 - rate, dt * 60); }

    pointer.x += (target.x - pointer.x) * damp(0.08);
    pointer.y += (target.y - pointer.y) * damp(0.08);

    /* the halves pull apart and the clip planes travel with them, so a real
       gap opens down the middle */
    var breathe = reduced ? 0 : Math.sin(t * 0.7) * 0.5 + 0.5;
    burst *= Math.pow(0.06, dt);
    var sep = split * (0.75 + breathe * 0.6) + burst * 0.95;

    left.position.x = -sep;
    right.position.x = sep;
    planeL.constant = -sep;
    planeR.constant = -sep;

    left.position.y = reduced ? 0 : Math.sin(t * 0.9) * 0.045;
    right.position.y = reduced ? 0 : Math.sin(t * 0.9 + 1.9) * 0.045;

    /* the crack flares crimson when you break it */
    var flare = 0.9 + burst * 2.4;
    eL.opacity = Math.min(1, flare);
    eR.opacity = Math.min(1, flare);
    mL.emissiveIntensity = (0.7 + burst * 1.8) * glow;
    mR.emissiveIntensity = mL.emissiveIntensity;

    /* drag-driven orbit with throw + slow idle drift */
    if (dragging) {
      var d = dragDX * 0.005;
      spinTo -= d;
      /* velocity in rad/sec, smoothed so one jittery sample cannot define the throw */
      spinVel += ((-d / dt) - spinVel) * damp(0.3);
      tiltY = Math.max(-1.1, Math.min(1.1, tiltY + dragDY * 0.0035));
      dragDX = 0;
      dragDY = 0;
      /* the cursor also drives a parallax offset; leaving it live while
         dragging made two inputs fight over the camera and read as wobble */
      parallax += (0 - parallax) * damp(0.18);
    } else {
      spinTo += spinVel * dt;
      spinVel *= Math.pow(0.04, dt);
      if (!reduced) spinTo += 0.16 * dt;
      tiltY *= Math.pow(0.45, dt);
      parallax += (1 - parallax) * damp(0.05);
    }

    /* one smoothing pass so raw input jitter never reaches the camera */
    spin += (spinTo - spin) * damp(0.25);

    var radius = fitRadius;
    camera.position.x = Math.sin(spin) * radius * 0.42 + pointer.x * 0.9 * parallax;
    camera.position.y = Math.sin(t * 0.32) * 0.35 - pointer.y * 0.7 * parallax + tiltY * 1.6;
    camera.position.z = Math.cos(spin) * radius;
    camera.lookAt(0, 0, 0);

    key.position.x = Math.cos(t * 0.5) * 4.2;
    key.position.z = Math.sin(t * 0.5) * 4.2 + 1.5;
    key.intensity = (26 + burst * 60) * glow;
    fill.position.x = Math.cos(t * 0.33 + 2.1) * 4.6;

    var p = pGeo.attributes.position;
    var step60 = dt * 60;
    for (var j = 0; j < count; j++) {
      var y = p.array[j * 3 + 1] + (0.0016 + seed[j] * 0.004 + burst * 0.02) * step60;
      if (y > 5) y = -5;
      p.array[j * 3 + 1] = y;
      p.array[j * 3] += Math.sin(t * 0.4 + seed[j] * 9) * 0.0009 * step60;
    }
    p.needsUpdate = true;
    embers.rotation.y = t * 0.012;

    renderer.render(scene, camera);
  }
  frame();

  root.classList.add('is-ready');

  return function dispose() {
    if (disposed) return;
    disposed = true;
    cancelAnimationFrame(raf);
    ro.disconnect();
    io.disconnect();
    canvas.removeEventListener('pointerdown', onDown);
    root.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    window.removeEventListener('pointercancel', onUp);
    canvas.removeEventListener('click', onClick);
    root.removeEventListener('pointerleave', onLeave);
    for (var k = 0; k < materials.length; k++) materials[k].dispose();
    geo.dispose();
    edgeGeo.dispose();
    pGeo.dispose();
    renderer.dispose();
    if (renderer.forceContextLoss) renderer.forceContextLoss();
  };
}

function mount(el) {
  if (el.__d3dDispose) return;
  try {
    el.__d3dDispose = mountDanachiHero(el) || null;
  } catch (err) {
    /* WebGL unavailable - the poster image behind the canvas stays visible */
    el.classList.add('is-fallback');
    console.warn('[danachi-3d]', err);
  }
}

function unmount(el) {
  if (el && typeof el.__d3dDispose === 'function') {
    el.__d3dDispose();
    el.__d3dDispose = null;
  }
}

function boot(scope) {
  (scope || document).querySelectorAll('[data-danachi-3d]').forEach(mount);
}

if (document.readyState !== 'loading') boot();
else document.addEventListener('DOMContentLoaded', function () { boot(); });

/* the theme editor swaps section markup in and out; tear the old context down
   or every edit stacks another live renderer until the browser drops them */
document.addEventListener('shopify:section:unload', function (e) {
  if (!e.target || !e.target.querySelectorAll) return;
  e.target.querySelectorAll('[data-danachi-3d]').forEach(unmount);
});
document.addEventListener('shopify:section:load', function (e) {
  boot(e.target && e.target.querySelectorAll ? e.target : document);
});
/* e.persisted means the page is entering the back/forward cache and will be
   restored intact - disposing here would leave a dead canvas on Back with no
   remount path, since DOMContentLoaded does not fire again */
window.addEventListener('pagehide', function (e) {
  if (e.persisted) return;
  document.querySelectorAll('[data-danachi-3d]').forEach(unmount);
});
